import { ChangeEventHandler, useCallback, useReducer } from "react";

import type { Hit } from "instantsearch.js";
import { Plus } from "lucide-react";

import { EmailMultiselect } from "@/components/pieces/form/EmailMultiselect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogInner,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

type Action =
  | { type: "SET_EMAILS"; emails: Hit[] }
  | { type: "SET_NOTE"; note?: string }
  | { type: "SET_SEND_NOTIFICATIONS"; sendNotifications: boolean }
  | { type: "SET_ERROR"; error?: string }
  | { type: "SET_OPEN"; open: boolean };

const initialState = {
  emails: [] as Hit[],
  note: undefined as string | undefined,
  sendNotifications: true,
  error: undefined as string | undefined,
  open: false,
};

const reducer = (state: typeof initialState, action: Action) => {
  switch (action.type) {
    case "SET_EMAILS":
      return { ...state, emails: action.emails, error: undefined };
    case "SET_NOTE":
      return { ...state, note: action.note, error: undefined };
    case "SET_SEND_NOTIFICATIONS":
      return {
        ...state,
        sendNotifications: action.sendNotifications,
        error: undefined,
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "SET_OPEN":
      return {
        ...initialState,
        open: action.open,
      };
    default:
      return state;
  }
};

export function IssueManuallyDialog({
  onIssueManually,
  workspaceId,
}: {
  onIssueManually: (
    userIds: string[],
    sendNotifications: boolean,
    onClose: (error?: string) => void,
    note?: string,
  ) => void;
  workspaceId?: string;
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleIssueManually = useCallback(
    () =>
      onIssueManually(
        state.emails.map((email) => email.objectID),
        state.sendNotifications,
        (error) =>
          error
            ? dispatch({ type: "SET_ERROR", error })
            : dispatch({ type: "SET_OPEN", open: false }),
        state.note,
      ),
    [onIssueManually, state.emails, state.sendNotifications, state.note],
  );
  const handleNoteChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    (evt) => dispatch({ type: "SET_NOTE", note: evt.target.value }),
    [dispatch],
  );

  const handleCheckSendNotifications = useCallback(
    (check: boolean) =>
      dispatch({ type: "SET_SEND_NOTIFICATIONS", sendNotifications: check }),
    [dispatch],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => dispatch({ type: "SET_OPEN", open }),
    [dispatch],
  );

  const handleEmailsChange = useCallback(
    (emails: Hit[]) => dispatch({ type: "SET_EMAILS", emails }),
    [dispatch],
  );

  return (
    <Dialog open={state.open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus strokeWidth={3} /> Issue Manually
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue Certifications</DialogTitle>
        </DialogHeader>
        <DialogInner>
          <div className="flex flex-col gap-6">
            {state.error && <div className="text-red-800">{state.error}</div>}
            <div className="flex flex-col gap-1">
              <div className="text-base/snug font-semibold text-gray-900">
                Users
              </div>
              <EmailMultiselect
                selections={state.emails}
                onChange={handleEmailsChange}
                filters={`workspace_${workspaceId}`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-base/snug font-semibold text-gray-900">
                Add Note (optional)
              </div>
              <div className="text-sm text-gray-500">
                This will only be visible to people in the workspace who have
                access to this certification
              </div>
              <Textarea
                placeholder="Add note"
                value={state.note}
                onChange={handleNoteChange}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={state.sendNotifications}
                onCheckedChange={handleCheckSendNotifications}
              />
              Send email notifications to recipients
            </div>
            <Button className="self-start" onClick={handleIssueManually}>
              Issue Certifications
            </Button>
          </div>
        </DialogInner>
      </DialogContent>
    </Dialog>
  );
}
